import { formattedDataSelect } from "@app/common/utils/utils";
import routes from "@app/config/routes";
import {
  useGetCashClosingConfig,
  useGetCompanyClientList,
  useGetPatientList
} from "@app/services/common/hooks";
import { useDeliveryData } from "@app/store/useDeliverySearchData";
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Image,
  Select,
  Text,
  TextInput,
  Title
} from "@mantine/core";
import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Fragment } from "react/jsx-runtime";
import toOrder from "../../../assets/toOrder.svg";
import SelectionDelivery from "../selection/Selection";
import InsuranceList from "./insurance/InsuranceList";
import QRScannerComponent from "./QRScanner";
import "./styles.scss";
import { IconHandGrab, IconSearch } from "@tabler/icons-react";
import { useAuthentication } from "@app/store/useAuthtentication";
import { useGenericData } from "@app/store/useGenericData";
import { warningNotification } from "@app/common/components/notifications";

const Order = () => {
  const navigate = useNavigate();
  const { idUser, idAccount } = useAuthentication((store) => store.auth.dataBiowel);
  const { siteId } = useGenericData((store) => store.genericData);

  const { saveDataToSearch } = useDeliveryData();
  const { type, corporateClient, patient } = useDeliveryData((state) => state.dataToSearch);
  const { showDeliveryBy, dataPatient, ...dataDispense } = useDeliveryData(
    (state) => state.dataDispense
  );
  const setDataDispense = useDeliveryData((store) => store.setDataDispense);
  const setDataToSearch = useDeliveryData((store) => store.saveDataToSearch);

  const setShowDelivery = (payload: {
    orderId: number | null;
    isPendingCompleteDelivery: boolean;
  }) => {
    setDataDispense({ ...dataDispense, dataPatient, showDeliveryBy: false, ...payload });
  };
  const [submittedSearch, setSubmittedSearch] = useState("");
  const [searchV, setSearchV] = useState("");
  const [typeOrder, setTypeOrder] = useState<"patient" | "corpClient">(type);
  const [qrScanner, setQrScanner] = useState<{
    isOpen: boolean;
  }>({
    isOpen: false
  });
  const [trigger, setTrigger] = useState(0);
  const [insuranceValue, setInsuranceValue] = useState<number | string>(
    patient !== "" ? patient : corporateClient !== "" ? corporateClient : ""
  );

  const {
    data: patientList = {
      results: [],
      rowTotal: 0
    },
    isFetching
  } = useGetPatientList({
    perpage: 100,
    page: 1,
    search: submittedSearch
  });
  const {
    data: insuranceList = {
      results: [],
      rowTotal: 0
    }
  } = useGetCompanyClientList({
    contractCompanyType: "",
    status: "enabled",
    eaccount: idAccount
  });

  const { data: cashClosingConfig } = useGetCashClosingConfig({
    userId: idUser,
    eaccount: idAccount,
    siteId: siteId ?? 0
  });

  const handleSubmit = (value: string) => {
    setQrScanner((state) => ({
      ...state,
      isOpen: false
    }));
    setTrigger(0);
    navigate(routes.getDeliveryDispensation, {
      state: { orderId: value }
    });
  };

  const onSubmitSearch = (event: FormEvent<HTMLDivElement>) => {
    event.preventDefault();
    const target = event.target as HTMLFormElement;
    const searchInput = target.elements.namedItem("search") as HTMLInputElement;

    setSubmittedSearch(searchInput.value.trim());
  };

  useEffect(() => {
    if (!cashClosingConfig?.results?.hasPendingCashClosing) {
      return;
    }

    warningNotification(
      "No es posible seguir dispensando, debe realizar un cierre parcial"
    ).finally(() => {
      navigate(routes.dispensation);
    });
  }, [navigate, cashClosingConfig]);

  const searchSection = () => {
    return (
      <>
        <QRScannerComponent
          key={trigger}
          open={qrScanner.isOpen}
          onClose={() => {
            setQrScanner((state) => ({
              ...state,
              isOpen: false
            }));
            setTrigger(0);
          }}
          handleSubmit={handleSubmit}
          setQrScanner={setTrigger}
          trigger={trigger}
        />
        <div className="searchSection">
          <Box>
            <Title
              pt={"xl"}
              mt={"sm"}
              pb={"xs"}
              px={"lg"}
              size={"h3"}
              c="secondary-color"
              fw={"bold"}
              ta={"start"}
            >
              Buscar ordenamiento
            </Title>
            <Box px={"lg"}>
              <div className="cardSearch">
                <Group component="form" align="end" gap="xs" onSubmit={onSubmitSearch}>
                  <TextInput
                    label="Nombre o ID del paciente"
                    flex={1}
                    placeholder="Escribir..."
                    value={searchV}
                    readOnly={isFetching}
                    name="search"
                    onChange={({ target }) => {
                      setSearchV(target.value);
                      setInsuranceValue("");
                      setTypeOrder("patient");
                      saveDataToSearch({
                        type: "patient",
                        patient: target.value,
                        corporateClient: ""
                      });
                    }}
                  />
                  <ActionIcon type="submit" disabled={isFetching} data-testid="order-search-button">
                    <IconSearch />
                  </ActionIcon>
                </Group>
                <Select
                  data-testid="order-insurance-select"
                  label="Aseguradora"
                  data={formattedDataSelect(insuranceList.results, "name", "id")}
                  searchable
                  value={String(insuranceValue)}
                  placeholder="Seleccionar..."
                  onChange={(_, value) => {
                    setSearchV("");
                    setInsuranceValue(value?.value ?? "");
                    setTypeOrder("corpClient");
                    saveDataToSearch({
                      type: "corpClient",
                      corporateClient: value.value,
                      patient: ""
                    });
                  }}
                />
              </div>
            </Box>
            {typeOrder !== "corpClient" && (
              <Box className="patientRow">
                <Box className="flex-column justify-center align-center ">
                  {patientList.results.length > 0 &&
                    patientList.results.map((patientInfo, idx) => {
                      return (
                        <div key={patientInfo.id} data-testid={`order-patient-row-${idx}`}>
                          <div>
                            <hr className="hr" />
                          </div>
                          <Box
                            px={"xs"}
                            key={patientInfo.id}
                            className="pointer"
                            onClick={() => {
                              setDataToSearch({
                                corporateClient,
                                patient,
                                type,
                                dataPatient: patientInfo
                              });
                              navigate(routes.getPatientOrderInformation);
                            }}
                            data-testid={`order-patient-box-${idx}`}
                          >
                            <Text fz={10} fw={"bold"}>
                              {patientInfo.first_name}
                              &nbsp;
                              {patientInfo.other_names}
                              &nbsp;
                              {patientInfo.last_name}
                              &nbsp;
                              {patientInfo.second_surname}
                            </Text>
                            <Text fz={10}>
                              {patientInfo.docTypeDesc}
                              &nbsp;
                              {patientInfo.document}
                            </Text>
                          </Box>
                        </div>
                      );
                    })}
                </Box>
              </Box>
            )}

            <Box className="flex-column justify-center align-center" mt={"xl"} px={"md"}>
              <Button
                size="sm"
                variant="outline"
                color="secondary-color.10"
                px="xl"
                onClick={() => {
                  setQrScanner((state) => ({
                    ...state,
                    isOpen: true
                  }));
                  setTrigger(trigger + 1);
                }}
              >
                Escanear código QR
              </Button>
            </Box>
            <Box className="flex-column justify-center align-center" mt={"xl"} px={"md"}>
              <Button
                size="sm"
                variant="outline"
                color="secondary-color.10"
                px="xl"
                data-testid="orderpatientlist-freesale-button"
                leftSection={<IconHandGrab />}
                onClick={() => {
                  setShowDelivery({
                    orderId: null,
                    isPendingCompleteDelivery: false
                  });
                  setDataDispense({
                    ...dataDispense,
                    deliveryBy: "freeSale",
                    dataPatient: dataPatient
                  });

                  navigate(routes.getDeliveryDispensation);
                }}
              >
                Dispensar por venta libre
              </Button>
            </Box>
          </Box>
          <Box className="flex items-end justify-center">
            <div className="h-100">
              <Image src={toOrder} />
            </div>
          </Box>
        </div>
      </>
    );
  };

  const renderComponent = () => {
    return (
      <Fragment>
        <div className="bodyOrder">
          {searchSection()}
          {showDeliveryBy ? (
            <SelectionDelivery />
          ) : (
            <div className="renderSection">
              {typeOrder === "corpClient" && (
                <InsuranceList type={typeOrder} corporateClient={insuranceValue} />
              )}
            </div>
          )}
        </div>
      </Fragment>
    );
  };

  return renderComponent();
};

export default Order;
